import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { MapPin, Phone, Mail, MessageCircle, Building2 } from 'lucide-react'
import { PageSeo, HeroSimple, Breadcrumbs, SectionHeading, ContactForm, Button } from '@/components/ui'
import { LocalizedLink } from '@/components/LocalizedLink'

const MAP_EMBED_URL =
  'https://www.google.com/maps/embed?origin=mfe&pb=!1m2!2m1!1sAD+Aesthetics+Clinic%2C+Via+Gazzera+1%2C+Verona'

const facilityQueries = [
  'Centro di Medicina Day Clinic, Via Monte Baldo, 12/B, Calzoni di Villafranca, Verona',
  'Casa di Cura San Francesco, Via Monte Ortigara, 21/B, 37127 Verona',
  'Medical Escape Clinic, Via Manzoni, 20, Milano',
]

const WHATSAPP_URL =
  'https://api.whatsapp.com/send/?phone=393666723741&text&type=phone_number&app_absent=0'

export function ContactsPage() {
  const { t, i18n } = useTranslation(['contacts', 'common'])

  const jsonLd = useMemo(
    () => [
      {
        '@context': 'https://schema.org',
        '@type': 'MedicalBusiness',
        name: 'AD Aesthetics',
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Via Gazzera, 1',
          addressLocality: 'Verona',
          postalCode: '37128',
          addressCountry: 'IT',
        },
        telephone: '+390458348902',
        email: 'info@adaesthetics.it',
      },
    ],
    []
  )

  const phoneNumber = t('common:contact.phone').replace(/\s/g, '')

  return (
    <>
      <PageSeo
        title={t('contacts:seo.title')}
        description={t('contacts:seo.description')}
        type="website"
        jsonLd={jsonLd}
        breadcrumb={[
          { name: t('common:routes.home'), path: `/${i18n.language}` },
          { name: t('common:routes.contacts') },
        ]}
      />

      <main className="min-h-screen bg-ink-deep">
        <HeroSimple
          title={t('contacts:hero.title')}
          subtitle={t('contacts:hero.subtitle')}
          variant="dark"
        />

        <Breadcrumbs
          items={[
            { label: t('common:routes.home'), to: 'home' },
            { label: t('common:routes.contacts') },
          ]}
        />

        <section className="py-16 md:py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <SectionHeading
              title={t('contacts:intro.title')}
              subtitle={t('contacts:intro.subtitle')}
              light
              centered
              className="mb-12 md:mb-16"
            />

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
              {/* Info sidebar */}
              <motion.aside
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ duration: 0.5 }}
                className="lg:col-span-5 space-y-6"
              >
                {/* Main contacts */}
                <div className="rounded-2xl border border-white/10 bg-ink p-6 md:p-8">
                  <h3 className="font-display text-xl md:text-2xl text-white mb-6">
                    {t('contacts:info.title')}
                  </h3>

                  <ul className="space-y-4">
                    <li className="flex items-start gap-3">
                      <div className="p-2 rounded-lg bg-primary/10 text-primary shrink-0 mt-0.5">
                        <MapPin className="w-5 h-5" />
                      </div>
                      <div>
                        <span className="block text-xs font-medium uppercase tracking-wide text-white/50 mb-0.5">
                          {t('contacts:info.addressLabel')}
                        </span>
                        <a
                          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent('Via Gazzera, 1, 37128 Verona, Italy')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-white/80 hover:text-primary transition-colors"
                        >
                          {t('contacts:info.address')}
                        </a>
                      </div>
                    </li>

                    <li className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-primary/10 text-primary shrink-0">
                        <Phone className="w-5 h-5" />
                      </div>
                      <div>
                        <span className="block text-xs font-medium uppercase tracking-wide text-white/50 mb-0.5">
                          {t('contacts:info.phoneLabel')}
                        </span>
                        <a
                          href={`tel:${phoneNumber}`}
                          className="text-white/80 hover:text-primary transition-colors"
                        >
                          {t('common:contact.phone')}
                        </a>
                      </div>
                    </li>

                    <li className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-primary/10 text-primary shrink-0">
                        <MessageCircle className="w-5 h-5" />
                      </div>
                      <div>
                        <span className="block text-xs font-medium uppercase tracking-wide text-white/50 mb-0.5">
                          {t('contacts:info.whatsappLabel')}
                        </span>
                        <a
                          href={WHATSAPP_URL}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-white/80 hover:text-primary transition-colors"
                        >
                          {t('contacts:info.whatsapp')}
                        </a>
                      </div>
                    </li>

                    <li className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-primary/10 text-primary shrink-0">
                        <Mail className="w-5 h-5" />
                      </div>
                      <div>
                        <span className="block text-xs font-medium uppercase tracking-wide text-white/50 mb-0.5">
                          {t('contacts:info.emailLabel')}
                        </span>
                        <a
                          href={`mailto:${t('common:contact.email')}`}
                          className="text-white/80 hover:text-primary transition-colors"
                        >
                          {t('common:contact.email')}
                        </a>
                      </div>
                    </li>
                  </ul>

                  <div className="mt-8 flex flex-col sm:flex-row gap-3">
                    <Button variant="primary-dark" asChild>
                      <a href={`tel:${phoneNumber}`}>{t('contacts:actions.call')}</a>
                    </Button>
                    <Button variant="outline-light" asChild>
                      <a
                        href={WHATSAPP_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {t('contacts:actions.whatsapp')}
                      </a>
                    </Button>
                  </div>
                </div>

                {/* Accredited facilities */}
                <div className="rounded-2xl border border-primary/20 bg-primary/5 p-6 md:p-8">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="p-2 rounded-lg bg-primary/10 text-primary">
                      <Building2 className="w-5 h-5" />
                    </div>
                    <h3 className="font-display text-xl text-white">
                      {t('contacts:info.facilitiesTitle')}
                    </h3>
                  </div>

                  <p className="text-white/70 text-sm mb-5">
                    {t('contacts:info.facilitiesNote')}
                  </p>

                  <ul className="space-y-3 text-sm text-white/80">
                    {facilityQueries.map((query, index) => (
                      <li key={index} className="flex gap-2">
                        <span className="text-primary mt-1">•</span>
                        <a
                          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:text-primary transition-colors"
                        >
                          {t(`contacts:info.facility${index + 1}` as const)}
                        </a>
                      </li>
                    ))}
                  </ul>

                  <div className="mt-6 pt-6 border-t border-white/10">
                    <LocalizedLink
                      to="bookConsultation"
                      className="inline-flex items-center text-sm font-medium text-primary hover:text-primary-light transition-colors"
                    >
                      {t('contacts:actions.book')}
                    </LocalizedLink>
                  </div>
                </div>
              </motion.aside>

              {/* Form */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="lg:col-span-7"
              >
                <ContactForm />
              </motion.div>
            </div>
          </div>
        </section>

        {/* Map */}
        <section className="w-full border-t border-primary/30" aria-label={t('contacts:map.title')}>
          <div className="w-full h-[24rem] md:h-[32rem] lg:h-[40rem]">
            <iframe
              src={MAP_EMBED_URL}
              title={t('contacts:map.title')}
              className="w-full h-full border-0"
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </section>
      </main>
    </>
  )
}
